import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  CalendarClock, Save, XCircle, Loader2, Bell, RotateCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { mesi: 4, label: '4 mesi', desc: 'Offerte con clausola cambio rapido' },
  { mesi: 6, label: '6 mesi', desc: 'Semestrale' },
  { mesi: 12, label: '12 mesi', desc: 'Annuale' },
  { mesi: 18, label: '18 mesi' },
  { mesi: 24, label: '24 mesi', desc: 'Biennale' },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

/**
 * Card gestione data ricontatto/rinnovo del contratto.
 * L'utente sceglie: preset mesi (4/6/12/18/24) OPPURE data manuale.
 * Il backend calcolera' data = created_at + mesi e la usera' nel check_rinnovi.
 */
const ContractRicontattoCard = ({ contractId, contract, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dataManuale, setDataManuale] = useState('');
  const [mesiSelected, setMesiSelected] = useState(null);
  const [note, setNote] = useState('');

  const reset = useCallback(() => {
    setDataManuale(contract?.data_ricontatto ? contract.data_ricontatto.slice(0, 10) : '');
    setMesiSelected(contract?.mesi_rinnovo || null);
    setNote(contract?.ricontatto_note || '');
    setEditing(false);
  }, [contract]);

  useEffect(() => { reset(); }, [reset]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { note: note || null };
      if (mesiSelected) payload.mesi_rinnovo = mesiSelected;
      else if (dataManuale) payload.data_ricontatto = dataManuale;
      else payload.data_ricontatto = null;
      await axios.put(`${API_URL}/api/contracts/${contractId}/ricontatto`, payload);
      toast.success('Data ricontatto salvata');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/contracts/${contractId}/ricontatto`, { data_ricontatto: null, mesi_rinnovo: null, note: null });
      toast.success('Promemoria rimosso');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error('Errore');
    } finally {
      setLoading(false);
    }
  };

  const dataRicontatto = contract?.data_ricontatto;
  const days = daysUntil(dataRicontatto);
  const isPast = days !== null && days < 0;
  const isSoon = days !== null && days >= 0 && days <= 60;

  return (
    <Card
      className={isPast ? 'border-red-200 bg-red-50/30' : isSoon ? 'border-amber-200 bg-amber-50/30' : ''}
      data-testid="ricontatto-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="w-5 h-5 text-sky-600" />
          Prossimo Ricontatto
          {dataRicontatto && !editing && (
            <>
              {isPast && <Badge className="bg-red-500 text-white ml-auto">Scaduta - da lavorare</Badge>}
              {!isPast && isSoon && <Badge className="bg-amber-500 text-white ml-auto">In arrivo</Badge>}
              {!isPast && !isSoon && <Badge className="bg-emerald-500 text-white ml-auto">Programmato</Badge>}
            </>
          )}
          {!dataRicontatto && !editing && (
            <Badge variant="outline" className="ml-auto text-slate-500">Non impostato</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!editing ? (
          <div className="space-y-2">
            {dataRicontatto ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Data:</span>
                  <span className="font-medium text-slate-800">{fmtDate(dataRicontatto)}</span>
                  {days !== null && (
                    <span className={`text-xs ${isPast ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-slate-500'}`}>
                      ({isPast ? `${Math.abs(days)} giorni fa` : days === 0 ? 'oggi' : `tra ${days} giorni`})
                    </span>
                  )}
                </div>
                {contract?.mesi_rinnovo && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <RotateCw className="w-3 h-3" />
                    Preset: {contract.mesi_rinnovo} mesi dalla data contratto
                  </div>
                )}
                {contract?.ricontatto_note && (
                  <p className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg p-2 mt-2">
                    <Bell className="w-3 h-3 inline mr-1 text-amber-500" />
                    {contract.ricontatto_note}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">Nessun promemoria impostato. Il sistema usera' il default globale (4 mesi).</p>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                type="button" size="sm"
                onClick={() => setEditing(true)}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="ricontatto-edit-btn"
              >
                <CalendarClock className="w-3.5 h-3.5 mr-1" />
                {dataRicontatto ? 'Modifica' : 'Imposta data ricontatto'}
              </Button>
              {dataRicontatto && (
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={handleReset}
                  disabled={loading}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="ricontatto-reset-btn"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Rimuovi
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ricontatto-form">
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">
                Scegli quando ricontattare
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(p => (
                  <button
                    key={p.mesi}
                    type="button"
                    onClick={() => { setMesiSelected(p.mesi); setDataManuale(''); }}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                      mesiSelected === p.mesi
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white border-slate-200 hover:border-sky-400 text-slate-700'
                    }`}
                    data-testid={`preset-mesi-${p.mesi}`}
                  >
                    {p.label}
                    {p.desc && <span className={`block text-[9px] mt-0.5 ${mesiSelected === p.mesi ? 'text-sky-100' : 'text-slate-400'}`}>{p.desc}</span>}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                La data viene calcolata da <strong>{contract?.created_at ? fmtDate(contract.created_at) : 'oggi'}</strong>
              </p>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <Label htmlFor="data-manuale" className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1 block">
                Oppure imposta una data specifica
              </Label>
              <Input
                id="data-manuale"
                type="date"
                value={dataManuale}
                onChange={(e) => { setDataManuale(e.target.value); setMesiSelected(null); }}
                data-testid="ricontatto-data-manuale"
              />
            </div>

            <div>
              <Label htmlFor="ricontatto-note" className="text-xs">Note (opzionale)</Label>
              <Textarea
                id="ricontatto-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="es. Cliente preferisce essere chiamato la mattina, oppure: valutare offerta Fastweb"
                rows={2}
                data-testid="ricontatto-note"
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button" size="sm"
                onClick={handleSave}
                disabled={loading || (!mesiSelected && !dataManuale)}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="ricontatto-save-btn"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Salva promemoria
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={reset} disabled={loading}>
                Annulla
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractRicontattoCard;
